import { safeStorage, type SafeStorageArea } from "./safeStorage";

/**
 * The "Try again" for an API read, which must reload the page when App Check
 * is why the read failed.
 *
 * WHY A REFETCH IS NOT ENOUGH. When the App Check exchange answers 403 (a
 * reCAPTCHA score under the project's threshold) or 404, the SDK refuses every
 * further exchange for ONE DAY, and it keeps that block on the provider object,
 * in page memory only (@firebase/app-check 0.13.0,
 * `ReCaptchaV3Provider._throttleData`; IndexedDB caches only the token). Every
 * later request in the same page goes out without the header and is refused
 * again, so an in-page retry fails for up to 24 hours. A reload builds a new
 * provider and makes one fresh exchange. The same holds when reCAPTCHA never
 * answered (blocked, timed out): a reload loads it again.
 *
 * Found on Prod 2026-09-30: a refused browser stayed on "Check your connection"
 * however often Try again was pressed.
 *
 * A person's click reloads, one exchange per click. The ONE exception is
 * autoReloadAfterFirstRefusal below (rc.15): a single automatic reload of a
 * page whose first read was refused, capped so it cannot loop, and used only
 * by Book's LoadFailed. When App Check is fine the retry is the in-page
 * refetch, unchanged: a network failure keeps the page and its state.
 *
 * SHARED BY BOTH SURFACES (moved from apps/book in c3). Each app's
 * getAppCheckHeaderToken records every attempt through
 * shared/lib/appCheckToken.ts, so on App and Book alike a screen's Try again
 * for an API read is `retryApiRead(refetch)`. Book's load failures reach it
 * through components/LoadFailed; App's status reads (payment setup, partner
 * onboarding) call it directly, and so does every payment-setup press that
 * mints an Account Link (safe to repeat, and no payment-setup route mints on
 * load, so the reload cannot loop).
 *
 * The record is page memory, like the SDK's block: a reload starts clean.
 * Only Lura API requests record here. Firebase Auth fetches its own App Check
 * token inside the SDK, so an Auth call (App's /auth/action inspection) leaves
 * no record and its retry is not an API read.
 *
 * No Firebase import on purpose: views read this without pulling Firebase into
 * their chunk.
 */

/**
 * Why the latest attempt produced no token, which decides the honest words:
 *
 * - `refused`: the exchange answered 403 or 404, or the SDK is holding the
 *   one-day block such an answer sets. The browser was refused; the network
 *   is fine.
 * - `unreachable`: reCAPTCHA never answered (blocked by an extension, a
 *   privacy setting or a network, or it timed out), or App Check could not
 *   start in this browser.
 * - `network`: the exchange could not be reached or answered with a server
 *   error. "Check your connection" is true here, so the old words stay.
 */
export type AppCheckFailure = "refused" | "unreachable" | "network";

let lastFailure: AppCheckFailure | null = null;
let attempted = false;
/** Whether any attempt on this page has produced a token. */
let succeeded = false;

/** Called after every token attempt (appCheckToken.ts): null on success. */
export function recordAppCheckAttempt(failure: AppCheckFailure | null): void {
  attempted = true;
  lastFailure = failure;
  if (failure === null) succeeded = true;
}

/**
 * reCAPTCHA's script loaded without defining `grecaptcha` (appCheckStart.ts).
 * Recorded as `unreachable` so a failed read's Try again reloads, unless a
 * token attempt has already been recorded: a cached or debug token may have
 * worked without reCAPTCHA, and the latest attempt is what the words and the
 * retry describe. The next attempt overwrites this either way.
 */
export function recordRecaptchaUnavailable(): void {
  if (!attempted) lastFailure = "unreachable";
}

/** Test-only: forget the record, as a reload does. */
export function resetAppCheckRecordForTests(): void {
  attempted = false;
  lastFailure = null;
  succeeded = false;
}

/** Whether this page's most recent App Check attempt produced no token. */
export function appCheckAttemptFailed(): boolean {
  return lastFailure !== null;
}

/** Whether any App Check attempt on this page has produced a token (a diagnostic; and the first-read test above). */
export function appCheckSucceededOnPage(): boolean {
  return succeeded;
}

/** Why it failed, or null when it did not. */
export function appCheckFailure(): AppCheckFailure | null {
  return lastFailure;
}

/**
 * Classifies what the App Check SDK threw. The codes and the `httpStatus`
 * field are the SDK's (`appCheck/<code>`, ERROR_FACTORY in
 * @firebase/app-check). In production the provider wraps every exchange
 * status error as `initial-throttle`; only 403 and 404 are refusals, and
 * anything else is the service having trouble.
 */
export function classifyAppCheckError(error: unknown): AppCheckFailure {
  const code = typeof error === "object" && error !== null && "code" in error ? String(error.code) : "";
  const data = typeof error === "object" && error !== null && "customData" in error ? error.customData : null;
  const status =
    typeof data === "object" && data !== null && "httpStatus" in data ? Number(data.httpStatus) : Number.NaN;
  if (/^appCheck\/(initial-throttle|throttled|fetch-status-error)$/.test(code)) {
    return status === 403 || status === 404 ? "refused" : "network";
  }
  if (code === "appCheck/recaptcha-error") return "unreachable";
  return "network";
}

export function retryApiRead(refetch: () => void, reload: () => void = () => window.location.reload()): void {
  if (lastFailure !== null) {
    reload();
    return;
  }
  refetch();
}

/**
 * The body to show instead of "Check your connection" when App Check is why a
 * read failed, or null to keep the existing words (no failure, or a genuine
 * network failure). The keys are Book's catalogue (P-15); App has no approved
 * App Check words yet, so App keeps its own failure line and only adopts the
 * retry.
 */
export function appCheckFailureBody(): "appcheck.refused.body" | "appcheck.unreachable.body" | null {
  if (lastFailure === "refused") return "appcheck.refused.body";
  if (lastFailure === "unreachable") return "appcheck.unreachable.body";
  return null;
}

/*
 * ONE AUTOMATIC RELOAD WHEN THE PAGE'S FIRST READ IS REFUSED (rc.15; the Web
 * Director, 2026-10-20, after Mobile's sitting: a first-visit Firefox Android
 * Custom Tab's reCAPTCHA v3 exchange was refused for a low score, the SDK set
 * its one-day in-page block, and the slug read went out without a token and
 * got a 403. The next open passed.) A reload makes one fresh exchange, which
 * is what Try again already does by hand.
 *
 * Only when ALL of these hold, otherwise the existing words and Try again:
 * - the failure is the classified `refused` (an exchange 403/404 or the SDK's
 *   block). Never `unreachable` (reCAPTCHA blocked: a reload loads it again
 *   into the same block) and never `network`;
 * - no attempt on this page has produced a token: the refused read is the
 *   page's FIRST, so no hold or booking has been made on it (both need one);
 * - this page (by name) has not auto-reloaded in the last AUTO_RELOAD_WINDOW_MS
 *   (a sessionStorage marker, this tab only), so a second refusal after the
 *   reload shows the existing words: one reload, never a loop;
 * - sessionStorage works. If it throws, no automatic reload (without the
 *   marker it could not be capped).
 * - and, independent of storage (security audit W-2, reviewer P2), this
 *   document is not itself a reload: a reloaded page never reloads itself,
 *   and the marker must read back exactly as written.
 * The key is the page's NAME the caller gives ("profile", "book",
 * "appointment"), never a path, so no bearer link is ever written down.
 */
export const AUTO_RELOAD_KEY = "lura.appcheck.autoReload";
/** The pages that may reload once (by name, never a path: /a's carries a bearer). */
export type AutoReloadPage = "profile" | "book" | "appointment";

/** Whether this document was itself loaded by a reload (Navigation Timing), or unknown (false). */
function documentWasReloaded(): boolean {
  try {
    const entry = globalThis.performance?.getEntriesByType?.("navigation")?.[0] as { type?: string } | undefined;
    return entry?.type === "reload";
  } catch {
    return false;
  }
}
export const AUTO_RELOAD_WINDOW_MS = 10 * 60 * 1000;

export function autoReloadAfterFirstRefusal(
  pattern: AutoReloadPage,
  {
    now = Date.now(),
    storage = safeStorage.session,
    reload = () => window.location.reload(),
    reloaded = documentWasReloaded,
  }: { now?: number; storage?: Pick<SafeStorageArea, "get" | "set">; reload?: () => void; reloaded?: () => boolean } = {}
): boolean {
  if (lastFailure !== "refused" || succeeded || reloaded()) return false;
  // safeStorage never throws: a read that fails reads as no marker, and then
  // the WRITE decides. The reload happens only once the marker is written, so
  // without working storage there is no automatic reload, and so no loop.
  let marks: Record<string, unknown> = {};
  const raw = storage.get(AUTO_RELOAD_KEY);
  if (raw !== null) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) return false;
      marks = parsed as Record<string, unknown>;
    } catch {
      return false;
    }
  }
  const last = typeof marks[pattern] === "number" ? (marks[pattern] as number) : null;
  // A marker dated ahead of now (a clock moved back, a hand-set value) also
  // suppresses it: what cannot be placed in the window is not "no recent
  // reload" (security audit W-1).
  if (last !== null && now - last < AUTO_RELOAD_WINDOW_MS) return false;
  const written = JSON.stringify({ ...marks, [pattern]: now });
  if (!storage.set(AUTO_RELOAD_KEY, written) || storage.get(AUTO_RELOAD_KEY) !== written) return false;
  reload();
  return true;
}
